// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Pure analysis functions. No DOM, no fetch — all unit tested. */
import type { Facility, Meta } from './types';
import { sectorColour, STATE_NAMES } from './sectors';

export function median(values: number[]): number {
  const v = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (v.length === 0) return 0;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

export function sum<T>(items: T[], fn: (t: T) => number): number {
  return items.reduce((s, t) => s + fn(t), 0);
}

/** An aggregate over a set of facilities (a sector, a company or a state). */
export interface Group {
  key: string;
  label: string;
  colour: string;
  count: number;
  covered: number;
  baseline: number;
  excess: number;
  co2: number;
  ch4: number;
  n2o: number;
  smcsIssued: number;
  unitsSurrendered: number;
  nOver: number;
  methaneShare: number | null;
  facilities: Facility[];
}

function makeGroup(key: string, label: string, colour: string, items: Facility[]): Group {
  const covered = sum(items, (f) => f.covered);
  const ch4 = sum(items, (f) => f.ch4);
  return {
    key,
    label,
    colour,
    count: items.length,
    covered,
    baseline: sum(items, (f) => f.baseline),
    excess: sum(items, (f) => f.excess),
    co2: sum(items, (f) => f.co2),
    ch4,
    n2o: sum(items, (f) => f.n2o),
    smcsIssued: sum(items, (f) => f.smcsIssued),
    unitsSurrendered: sum(items, (f) => f.unitsSurrendered),
    nOver: items.filter((f) => f.excess > 0.5).length,
    methaneShare: covered > 0 ? (ch4 / covered) * 100 : null,
    facilities: items.slice().sort((a, b) => b.covered - a.covered),
  };
}

function groupBy(
  all: Facility[],
  keyFn: (f: Facility) => string,
  labelFn: (f: Facility) => string,
  colourFn: (key: string, label: string) => string,
): Group[] {
  const buckets = new Map<string, Facility[]>();
  for (const f of all) {
    const k = keyFn(f);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(f);
  }
  const out: Group[] = [];
  for (const [k, items] of buckets) {
    out.push(makeGroup(k, labelFn(items[0]), colourFn(k, labelFn(items[0])), items));
  }
  return out.sort((a, b) => b.covered - a.covered);
}

export function bySector(all: Facility[]): Group[] {
  return groupBy(all, (f) => f.sector, (f) => f.sector, (_k, label) => sectorColour(label));
}

export function byCompany(all: Facility[]): Group[] {
  return groupBy(all, (f) => f.companyKey || f.company, (f) => f.company, (_k, label) => sectorColour(label));
}

/** Single-state groups only (MULTI facilities excluded from the map join). */
export function byState(all: Facility[]): Group[] {
  // States are shaded by the chosen map metric, so the group colour is unused;
  // give a neutral so callers never read a misleading fixed hue.
  return groupBy(
    all.filter((f) => f.state !== 'MULTI'),
    (f) => f.state,
    (f) => STATE_NAMES[f.state] ?? f.state,
    () => '#0ea5e9',
  );
}

export const MULTI_LABEL = STATE_NAMES.MULTI;

// ── Rankings ──────────────────────────────────────────────────────────────────
export type FacilityMetric = 'covered' | 'excess' | 'baseline' | 'ch4' | 'methaneShare' | 'unitsSurrendered' | 'smcsIssued';

export function facilityMetric(f: Facility, m: FacilityMetric): number | null {
  const v = f[m];
  return v == null || !Number.isFinite(v) ? null : v;
}

export function rankedBy(all: Facility[], metric: FacilityMetric, dir: 'desc' | 'asc' = 'desc'): Facility[] {
  const withVal = all.filter((f) => facilityMetric(f, metric) != null);
  withVal.sort((a, b) => {
    const av = facilityMetric(a, metric) as number;
    const bv = facilityMetric(b, metric) as number;
    return dir === 'desc' ? bv - av : av - bv;
  });
  return withVal;
}

// ── Histogram ─────────────────────────────────────────────────────────────────
export interface Bin {
  from: number;
  to: number;
  count: number;
  items: Facility[];
}

/** Equal-width histogram over a value fn, clamped to [min,max]. */
export function histogram(items: Facility[], value: (f: Facility) => number | null, binCount: number, min: number, max: number): Bin[] {
  const width = (max - min) / binCount;
  const bins: Bin[] = Array.from({ length: binCount }, (_, i) => ({ from: min + i * width, to: min + (i + 1) * width, count: 0, items: [] }));
  for (const f of items) {
    const v = value(f);
    if (v == null || !Number.isFinite(v)) continue;
    let idx = Math.floor((v - min) / width);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
    bins[idx].items.push(f);
  }
  return bins;
}

// ── Insights ──────────────────────────────────────────────────────────────────
export type Severity = 'info' | 'warn' | 'alert' | 'good';
export interface Insight {
  severity: Severity;
  title: string;
  body: string;
  facilities: string[]; // slugs
}

const mt = (t: number) => `${(t / 1e6).toFixed(1)} Mt`;
const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

/** Auto-detected findings, ordered most striking first. */
export function buildInsights(all: Facility[], meta: Meta): Insight[] {
  const out: Insight[] = [];
  const n = meta.national;

  // 1. Two-thirds emitted above the line.
  out.push({
    severity: 'alert',
    title: `${n.nOver} of ${n.facilities} facilities emitted above their baseline`,
    body:
      `Australia’s biggest emitters collectively released ${mt(n.totalCovered)} of CO₂-equivalent against an ` +
      `aggregate baseline of ${mt(n.totalBaseline)} — ${mt(n.totalExcess)} over. ${pct(n.nOver, n.facilities).toFixed(0)}% of ` +
      `covered facilities came in above their own declining cap this year, even as the scheme tightens toward net zero.`,
    facilities: rankedBy(all, 'excess').slice(0, 4).map((f) => f.slug),
  });

  // 2. Methane.
  const topCh4 = rankedBy(all.filter((f) => f.covered > 200_000), 'methaneShare').slice(0, 1)[0];
  out.push({
    severity: 'warn',
    title: `Methane is ${n.methaneShare.toFixed(0)}% of these emissions — ${mt(n.ch4)} of it`,
    body:
      `Beyond the carbon dioxide, ${mt(n.ch4)} of the total is fugitive methane leaking from coal mines and gas ` +
      `operations. ` +
      (topCh4 ? `${topCh4.name} runs the highest methane share of any large facility at ${(topCh4.methaneShare ?? 0).toFixed(0)}%. ` : '') +
      `Tonne for tonne methane warms far more than CO₂, so where it dominates it dominates the climate impact.`,
    facilities: topCh4 ? [topCh4.slug] : [],
  });

  // 3. Coal mining dominance.
  const sectors = bySector(all);
  const coal = sectors.find((s) => /coal mining/i.test(s.label));
  if (coal) {
    out.push({
      severity: 'info',
      title: `Coal mining is the single biggest emitting industry`,
      body:
        `Its ${coal.count} covered coal mines released ${mt(coal.covered)} — ${pct(coal.covered, n.totalCovered).toFixed(0)}% of all ` +
        `Safeguard emissions — and ${coal.nOver} of them emitted above baseline. Fugitive methane, not the diesel in the ` +
        `machines, is what makes underground coal mines such large emitters.`,
      facilities: coal.facilities.slice(0, 4).map((f) => f.slug),
    });
  }

  // 4. The carbon market: more units surrendered than credits earned.
  out.push({
    severity: 'info',
    title: `${(n.unitsSurrendered / 1e6).toFixed(1)}M carbon credits were surrendered to cover excess`,
    body:
      `Facilities that beat their baseline earned ${(n.smcsIssued / 1e6).toFixed(1)}M Safeguard Mechanism Credits, while ` +
      `those over the line surrendered ${(n.unitsSurrendered / 1e6).toFixed(1)}M credits (mostly ACCUs bought from elsewhere ` +
      `in the economy) to square their obligations. The gap is the scheme leaning on offsets rather than on-site cuts.`,
    facilities: rankedBy(all, 'unitsSurrendered').slice(0, 4).map((f) => f.slug),
  });

  // 5. Biggest single emitter.
  const top = rankedBy(all, 'covered')[0];
  if (top) {
    out.push({
      severity: 'info',
      title: `${top.name} is the country’s single largest covered facility`,
      body:
        `Operated by ${top.company}, it released ${mt(top.covered)} of CO₂-equivalent — ${pct(top.covered, n.totalCovered).toFixed(1)}% ` +
        `of the national Safeguard total on its own, and ${top.excess > 0 ? `${(top.excess / 1e3).toFixed(0)}k tonnes above` : 'under'} its ` +
        `${(top.baseline / 1e6).toFixed(1)} Mt baseline.`,
      facilities: [top.slug],
    });
  }

  // 6. Biggest year-on-year jump.
  const movers = all.filter((f) => f.coveredChange != null && Number.isFinite(f.coveredChange));
  if (movers.length) {
    const up = movers.slice().sort((a, b) => (b.coveredChange as number) - (a.coveredChange as number))[0];
    if (up && (up.coveredChange as number) > 0) {
      out.push({
        severity: 'warn',
        title: `${up.name} had the biggest emissions jump since ${meta.prevYear}`,
        body:
          `Its covered emissions rose ${(((up.coveredChange as number) / (up.prevCovered || 1)) * 100).toFixed(0)}% year on year, ` +
          `from ${mt(up.prevCovered as number)} to ${mt(up.covered)}. Year-on-year swings at a single facility usually track ` +
          `production — more ore, gas or clinker out the gate — rather than a change in how cleanly it runs.`,
        facilities: [up.slug],
      });
    }
  }

  return out;
}
